import { ScoreType } from "@modules/scoreboard/scoreTypes"
import { Exercise, ExerciseDraft } from "@modules/sessionBlock/exercises/exerciseTypes"
import {
  convertCommonExerciseToDto,
  convertExerciseFromDto,
  ExerciseDraftDtoSchema,
  ExerciseDtoSchema,
} from "@modules/sessionBlock/exercises/services/exercisesDto"
import { MediaDraftDtoSchema, MediaDtoSchema } from "@modules/sessionBlock/media/mediaDto"
import { Media, MediaDraft } from "@modules/sessionBlock/media/mediaTypes"
import {
  AthleteNote,
  AthleteNoteDraft,
  AthleteNoteDraftDtoSchema,
  AthleteNoteDtoSchema,
  CoachNote,
  CoachNoteDraft,
  CoachNoteDraftDtoSchema,
  CoachNoteDtoSchema,
} from "@modules/sessionBlock/notes/noteTypes"
import { z } from "zod"

export enum SessionBlockType {
  SESSION_EXERCISE = "SESSION_EXERCISE",
  COACH_NOTE = "COACH_NOTE",
  ATHLETE_NOTE = "ATHLETE_NOTE",
  MEDIA = "MEDIA",
  MEDIAS = "MEDIAS",
}

export type SessionBlockDraft =
  | {
      type: SessionBlockType.COACH_NOTE
      content: CoachNoteDraft
    }
  | {
      type: SessionBlockType.SESSION_EXERCISE
      content: ExerciseDraft
    }
  | {
      type: SessionBlockType.ATHLETE_NOTE
      content: AthleteNoteDraft
    }
  | {
      type: SessionBlockType.MEDIAS
      content: MediaDraft
    }

export type SessionBlockExercise = {
  type: SessionBlockType.SESSION_EXERCISE
  content: Exercise
}
export type SessionBlockCoachNote = {
  type: SessionBlockType.COACH_NOTE
  content: CoachNote
}
export type SessionBlockAthleteNote = {
  type: SessionBlockType.ATHLETE_NOTE
  content: AthleteNote
}
export type SessionBlockMedias = {
  type: SessionBlockType.MEDIAS
  content: { medias: Media[] }
}

export type SessionBlock = SessionBlockExercise | SessionBlockCoachNote | SessionBlockAthleteNote | SessionBlockMedias

export const SessionBlockDtoSchema = z
  .object({
    type: z.literal(SessionBlockType.COACH_NOTE),
    content: CoachNoteDtoSchema,
  })
  .or(
    z.object({
      type: z.literal(SessionBlockType.SESSION_EXERCISE),
      content: ExerciseDtoSchema,
    }),
  )
  .or(
    z.object({
      type: z.literal(SessionBlockType.ATHLETE_NOTE),
      content: AthleteNoteDtoSchema,
    }),
  )
  .or(
    z.object({
      type: z.literal(SessionBlockType.MEDIA),
      content: MediaDtoSchema,
    }),
  )
  .or(
    z.object({
      type: z.literal(SessionBlockType.MEDIAS),
      content: z.object({ medias: z.array(MediaDtoSchema) }),
    }),
  )

export type SessionBlockDto = z.infer<typeof SessionBlockDtoSchema>

export const SessionBlockDraftDtoSchema = z
  .object({
    type: z.literal(SessionBlockType.COACH_NOTE),
    content: CoachNoteDraftDtoSchema,
  })
  .or(
    z.object({
      type: z.literal(SessionBlockType.SESSION_EXERCISE),
      content: ExerciseDraftDtoSchema,
    }),
  )
  .or(
    z.object({
      type: z.literal(SessionBlockType.ATHLETE_NOTE),
      content: AthleteNoteDraftDtoSchema,
    }),
  )
  .or(
    z.object({
      type: z.literal(SessionBlockType.MEDIAS),
      content: MediaDraftDtoSchema,
    }),
  )

export type SessionBlockDraftDto = z.infer<typeof SessionBlockDraftDtoSchema>

export function convertSessionBlockToDraftDto(block: SessionBlockDraft): SessionBlockDraftDto {
  switch (block.type) {
    case SessionBlockType.SESSION_EXERCISE:
      return {
        type: SessionBlockType.SESSION_EXERCISE,
        content: {
          ...convertCommonExerciseToDto(block.content),
          scoreType: block.content.scoreType ?? ScoreType.NO_SCORE,
        },
      }
    // Weirdly typescript doesn't like a single return statement here
    case SessionBlockType.COACH_NOTE:
      return {
        type: block.type,
        content: block.content,
      }
    case SessionBlockType.ATHLETE_NOTE:
      return {
        type: block.type,
        content: block.content,
      }
    case SessionBlockType.MEDIAS:
      return {
        type: block.type,
        content: block.content,
      }
  }
}

export function convertSessionBlockFromDto(dto: SessionBlockDto): SessionBlock {
  switch (dto.type) {
    case SessionBlockType.COACH_NOTE:
      return {
        type: SessionBlockType.COACH_NOTE,
        content: {
          ...CoachNoteDtoSchema.parse(dto.content),
          durationInMin: dto.content.durationInMin ?? undefined,
        },
      }
    case SessionBlockType.ATHLETE_NOTE:
      return {
        type: SessionBlockType.ATHLETE_NOTE,
        content: AthleteNoteDtoSchema.parse(dto.content),
      }
    case SessionBlockType.MEDIA:
      return {
        type: SessionBlockType.MEDIAS,
        content: MediaDtoSchema.parse({ medias: [dto.content] }),
      }
    case SessionBlockType.MEDIAS:
      return {
        type: SessionBlockType.MEDIAS,
        content: MediaDtoSchema.parse(dto.content),
      }
    case SessionBlockType.SESSION_EXERCISE:
      return {
        type: SessionBlockType.SESSION_EXERCISE,
        content: convertExerciseFromDto(dto.content),
      }
  }
}
